import { useCallback, useMemo } from 'react';
import { Box, Checkbox } from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AlertOutlineIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import InputSelectComponent from 'UiKit/input-select';
import InputDatepicker from 'UiKit/input-datepicker';
import { TextRegular, TextSemiBold } from 'UiKit/typography';
import Tooltip from 'UiKit/tooltip';

import useGetHostList from 'Domain/brief-live/useGetHostList';
import useGetAvailableBriefDates from 'Domain/brief-live/useGetAvailableBriefDates';
import { CALENDAR_STATUS_CONFIG } from 'View/brief-live/const/statusConfig';
import getCalendarDayTooltip from 'View/brief-live/helper/getCalendarDayTooltip';
import formatDisplayDate from 'View/brief-live/helper/formatDisplayDate';
import getTimeOptions from 'View/brief-live/helper/getTimeOptions';
import filterEndTimeOptions from 'View/brief-live/helper/filterEndTimeOptions';

import styles from './styles.module.css';

const TIME_OPTIONS = getTimeOptions();

const TimeSelectField = ({ label, date, name, control, options, isDisabled }) => (
  <Box className={styles['time-section__container']}>
    <Box className={styles['time-label-col__left']}>
      <TextSemiBold>
        {label}
        <span>*</span>
      </TextSemiBold>
    </Box>
    <Box className={styles['time-label-col__right']}>
      <TextRegular>{formatDisplayDate(date)}</TextRegular>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <InputSelectComponent
            className={styles['brief-config-card__input']}
            value={field.value}
            placeholder="00:00"
            options={options}
            onChange={field.onChange}
            isDisabled={isDisabled}
            isSearchable={false}
          />
        )}
      />
    </Box>
  </Box>
);

const BriefConfigCard = ({ control, errors, formField, optionsBrand, isLoadingTalkingPointDetail, setValue }) => {
  const { t } = useTranslation('desktop-brief-live');

  const brandOptions = useMemo(() => optionsBrand?.map((brand) => ({ value: brand.id, label: brand.label })), [optionsBrand]);

  const { data: hostOptions, isLoading: isLoadingHost } = useGetHostList({
    queryParams: { brand: formField?.brand },
    isEnabled: !!formField?.brand && !!formField?.isSpecialEvent,
  });
  const { data: availableBriefDates = [] } = useGetAvailableBriefDates({
    brandId: formField?.brand,
    isSpecialEvent: formField?.isSpecialEvent,
    hostIds: formField?.host?.map((host) => host.value),
  });

  const calendarStatuses = useMemo(
    () => availableBriefDates.map((item) => ({ ...item, ...(CALENDAR_STATUS_CONFIG[item.status] || { blocked: false }) })),
    [availableBriefDates],
  );

  const getCalendarDayTooltipLocalized = useCallback((entry) => getCalendarDayTooltip(entry, t), [t]);

  const endTimeOptions = useMemo(
    () => filterEndTimeOptions(TIME_OPTIONS, formField?.startTime, formField?.displayDateRange?.[0], formField?.displayDateRange?.[1]),
    [formField?.startTime, formField?.displayDateRange],
  );

  return (
    <Box className={styles['brief-config-card__container']}>
      <Controller
        control={control}
        name="brand"
        render={({ field }) => (
          <InputSelectComponent
            {...field}
            label={t('formTalkingPoint.brand')}
            placeholder={t('formTalkingPoint.brandPlaceholder')}
            options={brandOptions}
            className={styles['brief-config-card__input']}
            error={errors?.brand}
            isFetching={isLoadingTalkingPointDetail}
            value={formField?.brand}
            onChange={(e) => {
              field.onChange(e);
              setValue('isSpecialEvent', false, { shouldDirty: true });
              setValue('host', [], { shouldDirty: true });
              setValue('displayDateRange', null, { shouldDirty: true });
              setValue('startTime', null, { shouldDirty: true });
              setValue('endTime', null, { shouldDirty: true });
              setValue('productHighlight', [], { shouldDirty: true });
            }}
            isRequired
          />
        )}
      />

      <Box className={styles['brief-config-card__checkbox-row']}>
        <Controller
          control={control}
          name="isSpecialEvent"
          render={({ field }) => (
            <Checkbox isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
              <TextSemiBold>{t('formTalkingPoint.specialEvent')}</TextSemiBold>
            </Checkbox>
          )}
        />
        <Tooltip
          align="left"
          label={
            <Box maxWidth="170px">
              <TextSemiBold>{t('formTalkingPoint.specialEventTooltipTitle')}</TextSemiBold>
              <TextRegular>{t('formTalkingPoint.specialEventTooltipContent')}</TextRegular>
            </Box>
          }
        >
          <AlertOutlineIcon style={{ marginTop: '2px' }} size="16px" color="var(--color-semantic-text-disabled)" />
        </Tooltip>
      </Box>

      <Condition statement={formField?.isSpecialEvent}>
        <Controller
          control={control}
          name="host"
          render={({ field }) => (
            <InputSelectComponent
              className={styles['brief-config-card__input']}
              error={errors?.host}
              label={t('formTalkingPoint.host')}
              value={field.value}
              placeholder={t('formTalkingPoint.hostPlaceholder')}
              options={hostOptions}
              isMulti={true}
              onChange={field.onChange}
              isLoading={isLoadingHost}
              isDisabled={!formField?.brand}
            />
          )}
        />
      </Condition>

      <Box className={styles['brief-config-card__date-time-container']}>
        <Controller
          control={control}
          name="displayDateRange"
          render={({ field }) => (
            <InputDatepicker
              label={t('formTalkingPoint.displayTime')}
              placeholder={t('formTalkingPoint.displayTimePlaceholder')}
              infoMessage={t('formTalkingPoint.displayTimeDescription')}
              className={styles['brief-config-card__input']}
              selectsRange
              shouldCloseOnSelect={true}
              dateFormat="dd MMM yy"
              minDate={new Date()}
              startDate={field.value?.[0]}
              endDate={field.value?.[1]}
              onChange={field.onChange}
              isDisabled={!formField?.brand || (formField?.isSpecialEvent ? !formField?.host?.length : false)}
              calendarStatuses={calendarStatuses}
              getCalendarDayTooltip={getCalendarDayTooltipLocalized}
              isRequired
            />
          )}
        />

        <Condition statement={formField?.isSpecialEvent}>
          <Box className={styles['input-time--container']}>
            <TimeSelectField
              label={t('formTalkingPoint.startTime')}
              date={formField?.displayDateRange?.[0]}
              name="startTime"
              control={control}
              options={TIME_OPTIONS}
              isDisabled={!formField?.displayDateRange?.[0]}
            />
            <TimeSelectField
              label={t('formTalkingPoint.endTime')}
              date={formField?.displayDateRange?.[1]}
              name="endTime"
              control={control}
              options={endTimeOptions}
              isDisabled={!formField?.displayDateRange?.[1]}
            />
          </Box>
        </Condition>
      </Box>
    </Box>
  );
};

export default BriefConfigCard;
